import { useEffect, useMemo, useState } from 'react'
import { Loader2, UserPlus, X } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { UserSearchForm, type UserSearchParams } from '@/pages/users/UserSearchForm'
import { useUsers } from '@/hooks/useUsers'
import type { UserListItem } from '@/api/users'
import { cn } from '@/lib/utils'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'

interface UserPickerDialogProps {
	open?: boolean
	onOpenChange?: (open: boolean) => void
	multiple?: boolean
	departmentId?: number | null
	value?: UserListItem | UserListItem[] | null
	onConfirm: (users: UserListItem[]) => void | Promise<void>
	title?: string
	confirmText?: string
	trigger?: React.ReactNode
	isSubmitting?: boolean
	/** 固定过滤的用户状态（如 1=仅在职），不传则不过滤 */
	status?: number
	/** 从候选列表排除的用户 ID（如排除转出员工本人） */
	excludeUserIds?: number[]
}

const getDepartmentLabel = (user: UserListItem) => {
	if (user.department_name) return user.department_name
	if (user.department_id) return `部门 #${user.department_id}`
	return '未分配部门'
}

export function UserPickerDialog({
	open: controlledOpen,
	onOpenChange,
	multiple = false,
	departmentId: controlledDepartmentId,
	value,
	onConfirm,
	title = '选择用户',
	confirmText = '确定',
	trigger,
	isSubmitting = false,
	status,
	excludeUserIds,
}: UserPickerDialogProps) {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
	const open = controlledOpen ?? uncontrolledOpen
	const setOpen = onOpenChange ?? setUncontrolledOpen
	const [searchValues, setSearchValues] = useState<UserSearchParams>({})
	const [appliedSearchValues, setAppliedSearchValues] = useState<UserSearchParams>({})
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
	const [selectedUsers, setSelectedUsers] = useState<UserListItem[]>([])

	useEffect(() => {
		if (!open) return
		if (!value) {
			setSelectedUsers([])
			return
		}
		setSelectedUsers(Array.isArray(value) ? value : [value])
	}, [open, value])

	const { data, isLoading, isFetching, isError, error, refetch } = useUsers({
		page,
		page_size: pageSize,
		...appliedSearchValues,
		...(controlledDepartmentId !== undefined && { department_id: controlledDepartmentId ?? undefined }),
		...(status !== undefined && { status }),
		enabled: open,
	})

	const users = useMemo(() => {
		const list = data?.list ?? []
		if (!excludeUserIds?.length) return list
		const excluded = new Set(excludeUserIds)
		return list.filter((user) => !excluded.has(user.id))
	}, [data?.list, excludeUserIds])
	const total = (data?.total ?? 0) - ((data?.list?.length ?? 0) - users.length)
	const selectedIds = useMemo(() => new Set(selectedUsers.map((user) => user.id)), [selectedUsers])

	const handleSearch = () => {
		setAppliedSearchValues(searchValues)
		setPage(1)
	}

	const handleReset = () => {
		setSearchValues({})
		setAppliedSearchValues({})
		setPage(1)
	}

	const handleToggleUser = (user: UserListItem) => {
		setSelectedUsers((prev) => {
			const exists = prev.some((item) => item.id === user.id)
			if (exists) return prev.filter((item) => item.id !== user.id)
			if (!multiple) return [user]
			return [...prev, user]
		})
	}

	const handleRemoveSelected = (userId: number) => {
		setSelectedUsers((prev) => prev.filter((user) => user.id !== userId))
	}

	const handleClose = () => {
		setOpen(false)
	}

	const handleConfirm = async () => {
		if (selectedUsers.length === 0) return
		await onConfirm(selectedUsers)
		if (controlledOpen === undefined) setOpen(false)
	}

	const selectionLabel = multiple ? `已选择 ${selectedUsers.length} 人` : selectedUsers[0]?.nickname || selectedUsers[0]?.username || '未选择'

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			{trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
			<DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-6xl overflow-hidden p-0 sm:max-w-6xl">
				<DialogHeader className="border-b px-6 py-4">
					<DialogTitle className="flex items-center gap-2">
						<UserPlus className="h-5 w-5" />
						{title}
					</DialogTitle>
				</DialogHeader>

				<div className="grid min-h-0 grid-cols-1 gap-0 lg:grid-cols-[minmax(0,1fr)_280px]">
					<div className="flex min-h-0 flex-col gap-4 p-6">
						<div className="rounded-lg border bg-card p-4">
							<UserSearchForm
								value={searchValues}
								onChange={setSearchValues}
								onSearch={handleSearch}
								onReset={handleReset}
							/>
						</div>

						<div className="min-h-0 overflow-hidden rounded-lg border">
							{isLoading ? (
								<div className="p-4 space-y-3">
									{Array.from({ length: 6 }).map((_, index) => (
										<div key={index} className="flex items-center gap-4">
											<Skeleton className="h-4 w-4" />
											<Skeleton className="h-4 w-28" />
											<Skeleton className="h-4 w-24" />
											<Skeleton className="h-4 flex-1" />
										</div>
									))}
								</div>
							) : isError ? (
								<div className="p-8 text-center">
									<p className="text-sm text-destructive">加载失败: {(error as Error).message}</p>
									<Button variant="outline" className="mt-4" onClick={() => refetch()}>
										重试
									</Button>
								</div>
							) : (
								<div className="max-h-[38vh] overflow-auto">
									<Table>
										<TableHeader className="sticky top-0 z-10 bg-background">
											<TableRow>
												<TableHead className="w-12"></TableHead>
												<TableHead>用户名</TableHead>
												<TableHead>昵称</TableHead>
												<TableHead>邮箱</TableHead>
												<TableHead>手机号</TableHead>
												<TableHead>部门</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{users.length === 0 ? (
												<TableRow>
													<TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
														暂无数据
													</TableCell>
												</TableRow>
											) : (
												users.map((user) => {
													const selected = selectedIds.has(user.id)
													return (
														<TableRow
															key={user.id}
															className={cn('cursor-pointer', selected && 'bg-muted/60')}
															onClick={() => handleToggleUser(user)}
														>
															<TableCell>
																<Checkbox
																	checked={selected}
																	onClick={(event) => event.stopPropagation()}
																	onCheckedChange={() => handleToggleUser(user)}
																/>
															</TableCell>
															<TableCell className="font-medium">{user.username}</TableCell>
															<TableCell>{user.nickname || '-'}</TableCell>
															<TableCell>{user.email || '-'}</TableCell>
															<TableCell>{user.phone || '-'}</TableCell>
															<TableCell>{user.department_name || (user.department_id ? `#${user.department_id}` : '-')}</TableCell>
														</TableRow>
													)
												})
											)}
										</TableBody>
									</Table>
								</div>
							)}
						</div>

						<ListPagination
							page={page}
							pageSize={pageSize}
							total={total}
							loading={isFetching}
							showPageNumbers={false}
							onPageChange={setPage}
							onPageSizeChange={setPageSize}
						/>
					</div>

					<aside className="flex min-h-0 flex-col border-t bg-muted/20 p-6 lg:border-l lg:border-t-0">
						<div className="flex items-center justify-between gap-3">
							<div>
								<p className="font-medium">{selectionLabel}</p>
								<p className="mt-1 text-xs text-muted-foreground">
									{multiple ? '可跨分页保留已选用户' : '单选模式会替换当前选择'}
								</p>
							</div>
							{selectedUsers.length > 0 && (
								<Button variant="ghost" size="sm" onClick={() => setSelectedUsers([])}>
									清空
								</Button>
							)}
						</div>

						<div className="mt-4 min-h-0 flex-1 space-y-2 overflow-auto">
							{selectedUsers.length === 0 ? (
								<div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
									暂无选择
								</div>
							) : (
								selectedUsers.map((user) => (
									<Badge key={user.id} variant="secondary" className="flex w-full items-start justify-between gap-2 px-3 py-2">
										<span className="min-w-0 text-left">
											<span className="block truncate">{user.nickname || user.username}</span>
											<span className="block truncate text-xs font-normal text-muted-foreground">
												{user.phone || '-'} · {getDepartmentLabel(user)}
											</span>
										</span>
										<X className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer" onClick={() => handleRemoveSelected(user.id)} />
									</Badge>
								))
							)}
						</div>
					</aside>
				</div>

				<DialogFooter className="border-t px-6 py-4">
					<Button type="button" variant="outline" onClick={handleClose}>
						取消
					</Button>
					<Button type="button" onClick={handleConfirm} disabled={selectedUsers.length === 0 || isSubmitting}>
						{isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{confirmText}{selectedUsers.length > 0 ? ` (${selectedUsers.length})` : ''}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
